import { Suspense, useEffect, useState } from 'react';

import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { PageFallback, UploadProgress } from '@components/index';
import { useBulkWorkspaceCreationProgress } from '@hooks/use-bulk-workspace-creation-progress';
import { useGetCustomer } from '@hooks/use-get-customer';
import { useGetPermissions } from '@hooks/use-get-permissions';
import { useGetUser } from '@hooks/use-get-user';
import { useGetWorkspace } from '@hooks/use-get-workspace';
import { customerAtom } from '@store/atoms/customer';
import { userProfileAtom } from '@store/atoms/user';
import { userPermissionsAtom } from '@store/atoms/userPermissions';
import { workspaceAtom } from '@store/atoms/workspace';
import {
  generateAndSaveTokenAndRedirectOnAccessFailure,
  isLoggedInUser,
} from '@utils/auth';
import SessionLogout from '@utils/session-logout/SessionLogout';

const LayoutWrapper = () => {
  const navigate = useNavigate();
  const { clientUuid } = useParams<{ clientUuid: string }>();

  const user = useRecoilValue(userProfileAtom);
  const currentWorkspace = useRecoilValue(workspaceAtom);
  const customerData = useRecoilValue(customerAtom);
  const permissions = useRecoilValue(userPermissionsAtom);

  const [isLoading, setIsLoading] = useState(true);
  const [tokenCheckComplete, setTokenCheckComplete] = useState(false);

  const { isLoading: isLoadingUser } = useGetUser({
    enabled: tokenCheckComplete && !user.id,
  });

  const { isLoading: isLoadingCustomer } = useGetCustomer({
    enabled: !!user.id && !customerData.id,
  });

  const { isLoading: isLoadingWorkspace } = useGetWorkspace({
    enabled: !!user.id && !currentWorkspace.id,
  });

  const { isLoading: isLoadingUserPermission } = useGetPermissions({
    enabled: !!user.id && !permissions.length,
  });

  useBulkWorkspaceCreationProgress();

  useEffect(() => {
    const initializeSession = async () => {
      if (isLoggedInUser() && clientUuid) {
        const tokenStatus =
          await generateAndSaveTokenAndRedirectOnAccessFailure({
            clientUuidFromUrl: clientUuid,
          });

        if (!tokenStatus.success && tokenStatus?.redirectUrl) {
          setIsLoading(false);
          navigate(tokenStatus.redirectUrl);

          return;
        }
      }

      setTokenCheckComplete(true);
    };

    initializeSession();
  }, [clientUuid, navigate]);

  useEffect(() => {
    if (
      user?.id &&
      currentWorkspace?.id &&
      permissions?.length &&
      !isLoadingUser &&
      !isLoadingCustomer &&
      !isLoadingWorkspace &&
      !isLoadingUserPermission
    )
      setIsLoading(false);
  }, [
    user?.id,
    currentWorkspace?.id,
    permissions,
    isLoadingUser,
    isLoadingWorkspace,
    isLoadingCustomer,
    isLoadingUserPermission,
  ]);

  if (isLoading) return <PageFallback />;

  return (
    <>
      <SessionLogout setShowFullLoader={setIsLoading} />
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
      <UploadProgress />
    </>
  );
};

export default LayoutWrapper;
